import React from "react";

import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";

import { Label } from "src/base-components/Label";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Switch } from "src/base-components/Switch";
import { ColumnConfigModal } from "src/cases/templates/caseBlocks/ColumnConfigModal";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import {
  TableColumnConfigInput,
  TableEditMode,
} from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { useModal } from "src/design-system/Modal";

type ColumnSetupMode = "default" | "custom";

type Props = {
  blockIndex: number;
};

export const TableBlockEditConfig: React.FC<Props> = ({ blockIndex }) => {
  const { setValue } = useFormContext<CaseTemplateFormValues>();
  const { isOpen, openModal, closeModal, afterLeave } = useModal();

  const editModeFieldPath =
    `attributes.case_blocks_configs.${blockIndex}.config.edit_mode` as const;
  const columnsFieldPath =
    `attributes.case_blocks_configs.${blockIndex}.config.columns` as const;
  const defaultColumnsEditModeFieldPath =
    `attributes.case_blocks_configs.${blockIndex}.config.default_columns_edit_mode` as const;

  const editMode = useWatch<CaseTemplateFormValues>({
    name: editModeFieldPath,
  }) as TableEditMode | undefined;

  const columns = useWatch<CaseTemplateFormValues>({
    name: columnsFieldPath,
  });

  const defaultColumnsEditable = useWatch<CaseTemplateFormValues>({
    name: defaultColumnsEditModeFieldPath,
    compute: (value) => value !== TableEditMode.READ_ONLY,
  }) as boolean;

  const isEditable = editMode === TableEditMode.EDITABLE;
  const columnSetupMode: ColumnSetupMode =
    columns !== null && columns !== undefined ? "custom" : "default";

  const handleToggle = (checked: boolean) => {
    setValue(
      editModeFieldPath,
      checked ? TableEditMode.EDITABLE : TableEditMode.READ_ONLY,
    );
    if (!checked) {
      setValue(defaultColumnsEditModeFieldPath, TableEditMode.READ_ONLY);
    }
  };

  const handleColumnSetupChange = (value: string) => {
    if (value === "default") {
      setValue(columnsFieldPath, null);
    } else {
      setValue(columnsFieldPath, {});
    }
  };

  const handleColumnsSave = (
    newColumns: { [key: string]: TableColumnConfigInput },
    defaultEditable: boolean,
  ) => {
    setValue(columnsFieldPath, newColumns);
    setValue(
      defaultColumnsEditModeFieldPath,
      defaultEditable ? TableEditMode.EDITABLE : TableEditMode.READ_ONLY,
    );
    closeModal();
  };

  const columnCount = columns ? Object.keys(columns).length : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label mb="mb-0">Allow reviewers to edit table values</Label>
          <Switch
            dataLoc="table-block-editable-toggle"
            enabled={isEditable}
            onChange={handleToggle}
          />
        </div>
        <p className="font-inter-normal-12px text-gray-500">
          When on, reviewers can edit this table's values during Case
          investigation. Each change is saved to Case history.
        </p>
      </div>

      {isEditable && (
        <div className="flex flex-col gap-2">
          <Label mb="mb-0">Column setup</Label>
          <SimpleRadioGroup
            orientation="vertical"
            value={columnSetupMode}
            onValueChange={handleColumnSetupChange}
          >
            <SimpleRadioGroup.Item
              dataLoc="table-column-setup-default"
              label={
                <div className="ml-2 flex flex-col">
                  <span className="font-inter-semibold-13px text-gray-800">
                    Use default columns
                  </span>
                  <span className="font-inter-normal-12px text-gray-500">
                    Every column is editable, with types read from the block
                    input.
                  </span>
                </div>
              }
              value="default"
              alignTop
            />
            <SimpleRadioGroup.Item
              dataLoc="table-column-setup-custom"
              label={
                <div className="ml-2 flex flex-col">
                  <span className="font-inter-semibold-13px text-gray-800">
                    Customize columns
                  </span>
                  <span className="font-inter-normal-12px text-gray-500">
                    Set column names, types and which columns are read-only.
                  </span>
                </div>
              }
              value="custom"
              alignTop
            />
          </SimpleRadioGroup>

          {columnSetupMode === "custom" && (
            <div className="ml-6">
              <Button
                dataLoc="table-add-columns-button"
                iconLeft={faEdit}
                size="sm"
                variant="secondary"
                onClick={() => openModal()}
              >
                {`Configure columns${columnCount > 0 ? ` (${columnCount})` : ""}`}
              </Button>
            </div>
          )}
        </div>
      )}

      <ColumnConfigModal
        afterLeave={afterLeave}
        columns={
          columns as
            | { [key: string]: TableColumnConfigInput }
            | null
            | undefined
        }
        defaultColumnsEditable={defaultColumnsEditable}
        isOpen={isOpen}
        onClose={closeModal}
        onSave={handleColumnsSave}
      />
    </div>
  );
};
